import { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { ChevronDown, Search } from "lucide-react";
import { cn } from "../../lib/utils";
import { Badge } from "./badge";
import { useAgents, useWorkflows } from "../../features/home/queries";
import { getWorkflowId } from "../../features/home/card-routes";

export interface AgentDropdownProps {
  value: { agent_id?: string; workflow_id?: string };
  onChange: (value: { agent_id?: string; agent_name?: string; workflow_id?: string }) => void;
  className?: string;
  align?: "left" | "right";
  includeAll?: boolean;
  exclude?: string[];
  placeholder?: string;
  hideWorkflows?: boolean;
}

export function AgentDropdown({
  value,
  onChange,
  className,
  align = "left",
  includeAll = true,
  exclude = [],
  placeholder,
  hideWorkflows = false,
}: AgentDropdownProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const btnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const [style, setStyle] = useState<React.CSSProperties>({});

  const agents = useAgents();
  const workflows = useWorkflows();

  const place = useCallback(() => {
    const r = btnRef.current?.getBoundingClientRect();
    if (!r) return;
    const MAX_H = 350;
    const below = window.innerHeight - r.bottom;
    const up = below < MAX_H + 12 && r.top > below;
    const w = Math.max(r.width, 240);
    const left = align === "right"
      ? Math.max(8, r.right - w)
      : Math.max(8, Math.min(r.left, window.innerWidth - w - 8));
    setStyle({
      position: "fixed",
      left,
      width: w,
      ...(up ? { bottom: window.innerHeight - r.top + 4 } : { top: r.bottom + 4 }),
      maxHeight: Math.min(MAX_H, (up ? r.top : below) - 12),
      zIndex: 9999,
    });
  }, [align]);

  useEffect(() => {
    if (!open) return;
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [open, place]);

  useEffect(() => {
    if (open) {
      setQuery("");
      // Portal content mounts this render; focus right after.
      requestAnimationFrame(() => searchRef.current?.focus());
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (!btnRef.current?.contains(t) && !menuRef.current?.contains(t)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  // Determine current label
  let currentLabel = includeAll ? (placeholder || "All agents") : (placeholder || "Select an agent…");
  if (value.agent_id) {
    const a = (agents.data || []).find(x => x.agent_id === value.agent_id);
    if (a) currentLabel = a.agent_name || a.name || "Untitled";
  } else if (value.workflow_id) {
    const w = (workflows.data || []).find(x => getWorkflowId(x) === value.workflow_id);
    if (w) currentLabel = w.name || w.agent_name || "Untitled";
  }

  const hasTargetFilter = !!value.agent_id || !!value.workflow_id;

  const q = query.trim().toLowerCase();
  const matches = (name: string) => !q || name.toLowerCase().includes(q);
  const filteredWorkflows = hideWorkflows
    ? []
    : (workflows.data || []).filter((w) => matches(w.name || w.agent_name || "Untitled"));
  const filteredAgents = (agents.data || [])
    .filter((agent) => !exclude.includes(agent.agent_id))
    .filter((agent) => matches(agent.agent_name || agent.name || "Untitled"));
  const noMatches = !!q && filteredWorkflows.length === 0 && filteredAgents.length === 0;

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen(o => !o)}
        className={cn(
          "flex w-full items-center justify-between gap-2 rounded-lg border border-line bg-soft px-3 py-2 text-sm text-ink transition-colors hover:bg-line/60 focus:outline-none focus:ring-2 focus:ring-brand/30",
          className
        )}
      >
        <span className="truncate">{currentLabel}</span>
        <ChevronDown className={cn("h-4 w-4 shrink-0 text-ink-3 transition-transform", open && "rotate-180")} />
      </button>

      {open && createPortal(
        <div
          ref={menuRef}
          style={style}
          className="flex flex-col overflow-y-auto overflow-x-hidden rounded-xl border border-line bg-elevated py-1.5 shadow-xl"
        >
          <div className="sticky top-0 z-10 flex items-center gap-2 border-b border-line bg-elevated px-3 py-2">
            <Search className="h-3.5 w-3.5 shrink-0 text-ink-3" />
            <input
              ref={searchRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Type to filter…"
              className="w-full bg-transparent text-sm text-ink focus:outline-none placeholder:text-ink-3"
            />
          </div>

          {includeAll && !q && (
            <button
              type="button"
              onClick={() => { onChange({ agent_id: undefined, agent_name: undefined, workflow_id: undefined }); setOpen(false); }}
              className={cn(
                "flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors hover:bg-soft",
                !hasTargetFilter && "bg-brand-soft font-semibold text-brand",
              )}
            >
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-line text-[10px] font-bold text-ink-2">∞</span>
              <span className="truncate">All agents</span>
            </button>
          )}

          {noMatches && (
            <p className="px-3 py-3 text-center text-xs text-ink-3">No agents match "{query.trim()}".</p>
          )}

          {filteredWorkflows.length > 0 && (
          <p className="border-t border-line px-3 pb-1 pt-2 font-mono text-[10px] font-semibold uppercase text-ink-3 mt-1">
            Multi-agents
          </p>
          )}
          {filteredWorkflows.map((w) => {
            const id = getWorkflowId(w);
            const name = w.name || w.agent_name || "Untitled";
            return (
              <button
                key={id}
                type="button"
                onClick={() => { onChange({ agent_id: undefined, agent_name: name, workflow_id: id }); setOpen(false); }}
                className={cn(
                  "flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors hover:bg-soft",
                  value.workflow_id === id && "bg-brand-soft font-semibold text-brand",
                )}
              >
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-[#ede9fe] text-[10px] font-bold text-[#7c3aed]">
                  {name.slice(0, 1)}
                </span>
                <span className="truncate">{name}</span>
                <Badge tone="violet" className="ml-auto shrink-0">Multi</Badge>
              </button>
            );
          })}
          
          {filteredAgents.length > 0 && (
          <p className="border-t border-line px-3 pb-1 pt-2 font-mono text-[10px] font-semibold uppercase text-ink-3 mt-1">
            Single agents
          </p>
          )}
          {filteredAgents.map((agent) => {
            const name = agent.agent_name || agent.name || "Untitled";
            return (
              <button
                key={agent.agent_id}
                type="button"
                onClick={() => { onChange({ agent_id: agent.agent_id, agent_name: name, workflow_id: undefined }); setOpen(false); }}
                className={cn(
                  "flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors hover:bg-soft",
                  value.agent_id === agent.agent_id && "bg-brand-soft font-semibold text-brand",
                )}
              >
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-soft text-[10px] font-bold text-ink">{name.slice(0, 1)}</span>
                <span className="truncate">{name}</span>
                <Badge tone="neutral" className="ml-auto shrink-0">Single</Badge>
              </button>
            );
          })}
        </div>,
        document.body
      )}
    </>
  );
}
